import { useEffect, useState } from "react";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Eye, Search, Download, Building2, Lock, X, Info, CalendarClock } from "lucide-react";

export default function ClientOnboarding() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [templates, setTemplates] = useState([]);

  const load = () => api.get("/clients?archived=false").then((r) => setClients(r.data));
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const openClient = async (c) => {
    setSelected(c);
    const r = await api.get(`/task-templates?client_id=${c.id}`);
    setTemplates(r.data);
  };

  const filtered = clients
    .filter((c) => c.company_name.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => a.company_name.localeCompare(b.company_name));

  const canViewPass = user.role === "SPV" || user.role === "Leader";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[var(--gold)] text-xs uppercase tracking-[0.2em] font-semibold">Feature 2</div>
          <h1 className="font-serif text-4xl mt-1">Client Onboarding</h1>
          <p className="text-[var(--text-secondary)] mt-1">{filtered.length} active clients · auto-sorted by name</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
            <input data-testid="client-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client…" className="pl-9 pr-4 py-2.5 rounded-xl border border-[var(--border-subtle)] bg-white text-sm outline-none focus:border-[var(--gold)] w-56" />
          </div>
          <button data-testid="export-excel-client-onboarding-button" onClick={() => downloadFile("/export/clients", "SV Clients.xlsx")} className="btn-gold px-4 py-2.5 text-sm flex items-center gap-2">
            <Download className="w-4 h-4" /> Excel
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
        {filtered.map((c) => (
          <div key={c.id} data-testid={`client-card-${c.id}`} className="card-elegant p-6 flex flex-col items-center text-center fade-up">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center overflow-hidden mb-4" style={{ background: "var(--gold-light)", border: "1px solid var(--border-gold)" }}>
              {c.logo ? <img src={c.logo} alt={c.company_name} className="w-full h-full object-cover" /> : <Building2 className="w-9 h-9 text-[var(--gold)]" />}
            </div>
            <div className="font-semibold text-[var(--text-primary)] leading-tight">{c.company_name}</div>
            <span className="mt-2 px-3 py-1 rounded-full text-xs font-medium bg-[var(--bg-subtle)] text-[var(--text-secondary)]">{c.category || "General"}</span>
            <button data-testid={`client-eye-${c.id}`} onClick={() => openClient(c)} className="mt-4 w-full py-2 rounded-lg border border-[var(--border-gold)] text-[var(--gold-hover)] text-sm font-medium flex items-center justify-center gap-2 hover:bg-[var(--gold)] hover:text-white transition">
              <Eye className="w-4 h-4" /> View Details
            </button>
          </div>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center py-12 text-[var(--text-muted)]">No clients found.</div>}
      </div>

      {selected && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(17,19,24,0.5)" }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto fade-up">
            <div className="gold-gradient px-6 py-5 flex items-center justify-between sticky top-0 z-10">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/90 flex items-center justify-center overflow-hidden">
                  {selected.logo ? <img src={selected.logo} className="w-full h-full object-cover" /> : <Building2 className="w-6 h-6 text-[var(--gold)]" />}
                </div>
                <div>
                  <div className="font-serif text-xl text-[#111318]">{selected.company_name}</div>
                  <div className="text-xs text-[#111318]/70">{selected.category}</div>
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="text-[#111318]/60 hover:text-[#111318]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-6">
              <section>
                <div className="flex items-center gap-2 mb-3 text-[var(--gold-hover)]"><Info className="w-4 h-4" /><span className="text-xs font-bold uppercase tracking-wider">Section 1 · Basic Information</span></div>
                <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
                  <Detail label="NPWP" value={selected.npwp} mono />
                  <Detail label="NIB Number" value={selected.nib} mono />
                  <Detail label="PIC Name" value={selected.pic_name} />
                  <Detail label="Email" value={selected.email} />
                  <Detail label="Phone" value={selected.phone} mono />
                  <Detail label="Address" value={selected.address} span />
                </div>
              </section>

              <section>
                <div className="flex items-center gap-2 mb-3 text-[var(--urgent)]"><Lock className="w-4 h-4" /><span className="text-xs font-bold uppercase tracking-wider">Section 2 · Password & Access</span></div>
                {canViewPass ? (
                  (selected.accounts && selected.accounts.length > 0) ? (
                    <div className="overflow-hidden rounded-xl border border-red-100">
                      <table className="w-full text-sm" data-testid="client-accounts-table">
                        <thead>
                          <tr className="bg-red-50/70 text-[var(--text-secondary)] text-xs uppercase text-left">
                            <th className="py-2.5 px-4">Name</th><th className="px-4">Username</th><th className="px-4">Password</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selected.accounts.map((a, i) => (
                            <tr key={i} className="border-t border-red-100">
                              <td className="py-2.5 px-4 font-medium">{a.name || "—"}</td>
                              <td className="px-4 font-mono text-xs">{a.username || "—"}</td>
                              <td className="px-4 font-mono text-xs">{a.password || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-sm text-[var(--text-muted)] bg-[var(--bg-subtle)] rounded-xl p-4">No access accounts recorded.</div>
                  )
                ) : (
                  <div className="text-sm text-[var(--text-muted)] bg-[var(--bg-subtle)] rounded-xl p-4 flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Restricted — only Leader & SPV can view access credentials.
                  </div>
                )}
              </section>

              <section>
                <div className="flex items-center gap-2 mb-3 text-[var(--teal)]"><CalendarClock className="w-4 h-4" /><span className="text-xs font-bold uppercase tracking-wider">Section 3 · Tax Services & Scheduled Tasks</span></div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {(selected.services || []).map((s) => <span key={s} className="px-3 py-1 rounded-full text-xs bg-teal-50 text-teal-700 font-medium">{s}</span>)}
                  {(!selected.services || selected.services.length === 0) && <span className="text-sm text-[var(--text-muted)]">No services defined</span>}
                </div>
                {templates.length > 0 ? (
                  <div className="space-y-2">
                    {templates.map((t) => (
                      <div key={t.id} className="flex items-center justify-between bg-[var(--bg-subtle)] rounded-lg px-3 py-2 text-sm">
                        <span className="font-medium">{t.name}</span>
                        <span className="text-xs px-2 py-0.5 rounded bg-white capitalize">{t.frequency} · until {t.active_until}</span>
                      </div>
                    ))}
                    <div className="text-xs text-[var(--text-muted)] pt-1">These auto-appear in the Project Tracker on schedule.</div>
                  </div>
                ) : <div className="text-sm text-[var(--text-muted)]">No scheduled tasks. Add them in Settings → Client → Tax & Scheduled Tasks.</div>}
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value, mono, span }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">{label}</div>
      <div className={`text-[var(--text-primary)] ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
